// Single source of truth for the on-disk series format. A series
// composes references to teams, grouped into classes. A spec series
// (F1, IndyCar) is a series with one class; multi-class endurance
// (IMSA, WEC) is a series with multiple classes.
//
// Class ids are local to the series — they don't need to be globally
// unique, only stable within the document so the UI can key rows
// reliably across re-renders. Team refs are by id (filename-derived,
// like teams/cars/drivers); the same team can appear in multiple
// series but must not appear in more than one class within the same
// series — loadSeries drops duplicates silently to keep races
// well-formed.
//
// Forward compatibility: additive fields only; loadSeries() backfills
// missing pieces. resolveSeries() is a separate pass that walks the
// refs against an already-loaded {teams} catalog and reports any
// missing ids — the roster picker can warn the same way loadTrack
// warns on formatMismatch.
//
// `defaultClassCarId` is reserved (see CLAUDE.md "schema is forward-
// compatible"): future spec-series enforcement can let a class
// override its teams' chassis pick. Today it has no effect; the team's
// own carId is still authoritative.

export const FORMAT_VERSION = 1;

export function newEmptySeries() {
  return {
    formatVersion: FORMAT_VERSION,
    name: '',
    shortName: '',                        // e.g. "IMSA"
    description: '',
    classes: [newEmptyClass('Class A')],  // one class by default — the
                                          // single-class series case
                                          // (most spec series) is
                                          // "don't think about classes".
    metadata: { createdAt: null, modifiedAt: null },
  };
}

export function newEmptyClass(name = '') {
  return {
    id: makeClassId(),
    name,
    teamIds: [],
    defaultClassCarId: null,              // reserved — see header notes
  };
}

// Stable-ish local id — collision-resistant within a single series
// document. Not cryptographically unique; doesn't need to be.
export function makeClassId() {
  return `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function loadSeries(data) {
  if (!data || typeof data !== 'object') {
    throw new Error('Not a valid series file: not an object');
  }
  if (typeof data.formatVersion !== 'number') {
    throw new Error('Not a valid series file: missing formatVersion');
  }
  if (typeof data.name !== 'string' || data.name.trim() === '') {
    throw new Error('Not a valid series file: missing name');
  }

  const merged = newEmptySeries();
  Object.assign(merged, data);

  if (typeof merged.shortName   !== 'string') merged.shortName   = '';
  if (typeof merged.description !== 'string') merged.description = '';

  // Normalize classes to a clean array of well-shaped objects. Drop
  // anything malformed silently — a series file with junk rows is
  // more useful loaded than rejected; resolveSeries() will surface
  // missing team refs separately.
  const rawClasses = Array.isArray(data.classes) ? data.classes : [];
  // Track team ids we've already accepted so a duplicate appearance
  // in a later class is dropped (see header: a team can't sit in two
  // classes at once within the same series).
  const seenTeamIds = new Set();
  merged.classes = rawClasses
    .filter(c => c && typeof c === 'object')
    .map(c => {
      const id = typeof c.id === 'string' && c.id ? c.id : makeClassId();
      const name = typeof c.name === 'string' ? c.name : '';
      const rawTeamIds = Array.isArray(c.teamIds) ? c.teamIds : [];
      const teamIds = [];
      for (const tid of rawTeamIds) {
        if (typeof tid !== 'string' || !tid) continue;
        if (seenTeamIds.has(tid)) continue;
        seenTeamIds.add(tid);
        teamIds.push(tid);
      }
      return {
        id,
        name,
        teamIds,
        defaultClassCarId: typeof c.defaultClassCarId === 'string' ? c.defaultClassCarId : null,
      };
    });

  // Ensure at least one class — an empty-classes file would render
  // nothing meaningful in the editor. Restoring the default keeps
  // the file editable.
  if (merged.classes.length === 0) {
    merged.classes = [newEmptyClass('Class A')];
  }

  if (!merged.metadata) merged.metadata = { createdAt: null, modifiedAt: null };

  return {
    series: merged,
    formatMismatch: data.formatVersion > FORMAT_VERSION ? data.formatVersion : null,
  };
}

// Resolve a series' team refs against an already-loaded catalog.
// Returns `{ resolved, missingRefs }` where `resolved` mirrors the
// series shape but each class carries `teams` (array of full team
// objects, in declaration order — missing ids dropped) alongside its
// `teamIds`. `missingRefs` lists ids that didn't resolve.
//
// `catalog.teams` is an id → object map (the loader pages build it
// from listTeams()).
export function resolveSeries(series, { teams = {} } = {}) {
  const missingRefs = [];
  const classes = series.classes.map(cls => {
    const teamObjs = [];
    for (const tid of cls.teamIds) {
      const team = teams[tid];
      if (team) teamObjs.push(team);
      else missingRefs.push({ kind: 'team', id: tid, classId: cls.id });
    }
    return { ...cls, teams: teamObjs };
  });
  return {
    resolved: { ...series, classes },
    missingRefs,
  };
}

// Convenience: every team id referenced anywhere in the series, in
// the order it appears. Used by the race-setup picker to expand a
// series → grid without flattening classes by hand.
export function allTeamIds(series) {
  const out = [];
  for (const cls of series.classes) {
    for (const tid of cls.teamIds) out.push(tid);
  }
  return out;
}
